import React, { useState, useEffect } from 'react';
import axios from 'axios';

const StatsPage = ({ trades, wallets }) => {
  const [timeFilter, setTimeFilter] = useState('all');
  const [solPrice, setSolPrice] = useState(null);
  const [loadingSolPrice, setLoadingSolPrice] = useState(true);
  const [tokenPrices, setTokenPrices] = useState({});
  const [apiError, setApiError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Fetch SOL price from CoinGecko with timeout and error handling
  const fetchSolPrice = async () => {
    try {
      setLoadingSolPrice(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd', {
        signal: controller.signal,
        timeout: 10000
      });
      
      clearTimeout(timeoutId);
      
      if (response.data.solana && response.data.solana.usd) {
        setSolPrice(response.data.solana.usd);
      } else {
        setSolPrice(null);
      }
    } catch (error) {
      console.error('Failed to fetch SOL price:', error);
      setSolPrice(null);
    } finally {
      setLoadingSolPrice(false);
    }
  };

  // Fetch token price from DexScreener with timeout and error handling
  const fetchTokenPrice = async (tokenAddress) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
      
      const response = await axios.get(`https://api.dexscreener.com/latest/dex/tokens/${tokenAddress}`, {
        signal: controller.signal,
        timeout: 8000
      });
      
      clearTimeout(timeoutId);
      
      if (response.data && response.data.pairs && response.data.pairs.length > 0) {
        const pair = response.data.pairs[0];
        return parseFloat(pair.priceUsd) || 0;
      }
      return 0;
    } catch (error) {
      console.error(`Failed to fetch price for token ${tokenAddress}:`, error);
      return 0;
    }
  };

  // Fetch prices for all active trades
  const fetchTokenPrices = async () => {
    const activeTrades = filteredTrades.filter(trade => 
      hasBuyTransactionIcon(trade) && !trade.sell_time && trade.token_address
    );
    
    const pricePromises = activeTrades.map(async (trade) => {
      const price = await fetchTokenPrice(trade.token_address);
      return { tokenAddress: trade.token_address, price };
    });
    
    try {
      const prices = await Promise.all(pricePromises);
      const priceMap = {};
      prices.forEach(({ tokenAddress, price }) => {
        priceMap[tokenAddress] = price;
      });
      setTokenPrices(priceMap);
    } catch (error) {
      console.error('Failed to fetch token prices:', error);
    }
  };

  // Filter trades by time
  const getFilteredTrades = () => {
    const now = new Date();
    const filterTime = new Date();
    
    switch (timeFilter) {
      case '1h':
        filterTime.setHours(now.getHours() - 1);
        break;
      case '6h':
        filterTime.setHours(now.getHours() - 6);
        break;
      case '24h':
        filterTime.setDate(now.getDate() - 1);
        break;
      case '7d':
        filterTime.setDate(now.getDate() - 7);
        break;
      case '30d':
        filterTime.setDate(now.getDate() - 30);
        break;
      default:
        return trades;
    }
    
    return trades.filter(trade => {
      const tradeTime = new Date(trade.buy_time || trade.created_at);
      return tradeTime >= filterTime;
    });
  };

  const filteredTrades = getFilteredTrades();

  // Helper function for holding percentage
  const getHoldingPercentage = (trade) => {
    // If no buy transaction, return 0
    if (!trade.buy_time) return 0;
    
    // For tokens with buy transactions, default to 100% if amounts are not properly set
    if (!trade.original_amount || trade.original_amount <= 0) {
      // If we have a buy transaction but no original_amount, assume 100% holding
      return 100;
    }
    
    const currentAmount = trade.amount || trade.original_amount || 0;
    return Math.round((currentAmount / trade.original_amount) * 100);
  };

  // Helper function to check if trade has buy transaction icon link
  const hasBuyTransactionIcon = (trade) => {
    return !!trade.transaction_id && !!trade.buy_time;
  };

  // Helper function to calculate P/L with fallback to current price calculation
  const calculateProfitLoss = (trade) => {
    if (trade == null) return 0;
    let profitLoss = trade.profit_loss;
    
    // For completed trades (with sell_time), always use the stored profit_loss
    if (trade.sell_time) {
      return profitLoss || 0;
    }
    
    // For active trades only: If profit_loss is 0, null, or undefined, calculate it from current price vs buy price
    if ((profitLoss === 0 || profitLoss === null || profitLoss === undefined) && 
        trade.buy_price && trade.buy_price > 0) {
      
      // Try to get real-time price from tokenPrices state
      const realTimePrice = tokenPrices[trade.token_address];
      
      // Use real-time price if available, otherwise use current_price, then buy_price as fallback
      const currentPrice = realTimePrice || trade.current_price || trade.buy_price;
      
      // Calculate P/L if we have a different price than buy price
      if (currentPrice !== trade.buy_price && currentPrice > 0) {
        profitLoss = ((currentPrice - trade.buy_price) / trade.buy_price) * 100;
      } else {
        // If prices are the same or invalid, P/L is 0
        profitLoss = 0;
      }
    }
    
    return profitLoss || 0;
  };

  // Calculate statistics
  const totalTrades = filteredTrades.length;
  const completedTrades = filteredTrades.filter(trade => hasBuyTransactionIcon(trade) && (getHoldingPercentage(trade) === 0 || trade.sell_time)).length;
  const activeTrades = filteredTrades.filter(trade => hasBuyTransactionIcon(trade) && !trade.sell_time).length;
  const nothingTrades = filteredTrades.filter(trade => !hasBuyTransactionIcon(trade)).length;
  const failedTrades = filteredTrades.filter(trade => 
    (trade.status === 'failed' || trade.failed) || 
    (trade.buy_time && !hasBuyTransactionIcon(trade)) || // Buy attempted but no transaction ID
    (trade.transaction_warning && trade.transaction_warning.includes('failed'))
  ).length;
  
  const profitableTrades = filteredTrades.filter(trade => calculateProfitLoss(trade) > 0).length;
  const lossTrades = filteredTrades.filter(trade => calculateProfitLoss(trade) < 0).length;
  
  const totalProfit = filteredTrades
    .filter(trade => calculateProfitLoss(trade) > 0)
    .reduce((sum, trade) => sum + calculateProfitLoss(trade), 0);
  
  const totalLoss = filteredTrades
    .filter(trade => calculateProfitLoss(trade) < 0)
    .reduce((sum, trade) => sum + Math.abs(calculateProfitLoss(trade)), 0);
  
  const winRate = completedTrades > 0 ? (profitableTrades / completedTrades) * 100 : 0;

  // Calculate additional statistics
  const avgProfitPerTrade = completedTrades > 0 ? (totalProfit - totalLoss) / completedTrades : 0;
  const bestTrade = filteredTrades.reduce((best, trade) => {
    if (best === null) return trade;
    if (calculateProfitLoss(trade) > calculateProfitLoss(best)) {
      return trade;
    }
    return best;
  }, null);
  const worstTrade = filteredTrades.reduce((worst, trade) => {
    if (worst === null) return trade;
    if (calculateProfitLoss(trade) < calculateProfitLoss(worst)) {
      return trade;
    }
    return worst;
  }, null);

  // Fetch data on component mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        setIsInitializing(true);
        setApiError(null);
        await fetchSolPrice(); // Fetch SOL price
      } catch (error) {
        console.error('Error initializing StatsPage:', error);
        setApiError('Failed to load initial data');
      } finally {
        setIsInitializing(false);
      }
    };
    
    initializeData();
    
    // Set up periodic refresh
    const interval = setInterval(() => {
      fetchSolPrice(); // Update SOL price every 30 seconds
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Fetch token prices when trades change
  useEffect(() => {
    if (filteredTrades.length > 0) {
      fetchTokenPrices();
    }
  }, [filteredTrades]);

  // Show loading state during initialization
  if (isInitializing) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-2">Statistics & Analytics</h1>
          <p className="text-slate-400">Loading trading performance data...</p>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-16 w-16 border-2 border-transparent" style={{ borderTopColor: '#22c55e', borderRightColor: '#22c55e' }}></div>
        </div>
      </div>
    );
  }

  // Show error state if API calls fail
  if (apiError) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-2">Statistics & Analytics</h1>
          <p className="text-slate-400">Comprehensive overview of your trading performance</p>
        </div>
        <div className="card">
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-red-400 mb-4">Data Loading Error</h2>
            <p className="text-red-300 mb-6">{apiError}</p>
            <button 
              onClick={() => window.location.reload()}
              className="btn btn-primary"
            >
              Retry Loading
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-white mb-2">Statistics & Analytics</h1>
        <p className="text-slate-400">Comprehensive overview of your trading performance</p>
        {loadingSolPrice && (
          <p className="text-yellow-400 text-sm mt-2">Loading SOL price...</p>
        )}
      </div>

      {/* Time Filter */}
      <div className="flex justify-center">
        <div className="backdrop-blur-sm rounded-lg p-2" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
          <div className="flex gap-3">
            {['all', '1h', '6h', '24h', '7d', '30d'].map((filter) => (
              <button
                key={filter}
                onClick={() => setTimeFilter(filter)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeFilter === filter
                    ? 'text-white'
                    : 'text-slate-300 hover:text-white'
                }`}
                style={timeFilter === filter ? { backgroundColor: '#22c55e' } : { backgroundColor: '#353535' }}
                onMouseEnter={(e) => { if (timeFilter !== filter) e.target.style.backgroundColor = '#22c55e'; }}
                onMouseLeave={(e) => { if (timeFilter !== filter) e.target.style.backgroundColor = '#353535'; }}
              >
                {filter === 'all' ? 'All Time' : filter === '1h' ? '1 Hour' : filter === '6h' ? '6 Hours' : filter === '24h' ? '24 Hours' : filter === '7d' ? '7 Days' : '30 Days'}
              </button>
            ))}
          </div>
        </div>
      </div>



      {/* Trading Statistics */}
      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-6">Trading Statistics</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Total Trades</div>
            <div className="text-2xl font-bold text-white">{totalTrades}</div>
            <div className="text-xs text-slate-500 mt-1">
              {timeFilter === 'all' ? 'All Time' : `Last ${timeFilter}`}
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Completed Trades</div>
            <div className="text-2xl font-bold text-green-400">{completedTrades}</div>
            <div className="text-xs text-slate-500 mt-1">
              {totalTrades > 0 ? `${((completedTrades / totalTrades) * 100).toFixed(1)}%` : '0%'}
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Active Trades</div>
            <div className="text-2xl font-bold text-purple-400">{activeTrades}</div>
            <div className="text-xs text-slate-500 mt-1">Currently Trading</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Failed Trades</div>
            <div className="text-2xl font-bold text-red-400">{failedTrades}</div>
            <div className="text-xs text-slate-500 mt-1">
              {totalTrades > 0 ? `${((failedTrades / totalTrades) * 100).toFixed(1)}%` : '0%'}
            </div>
          </div>
        </div>

        {/* Profit/Loss Analysis */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Win Rate</div>
            <div className="text-3xl font-bold text-green-400">{winRate.toFixed(1)}%</div>
            <div className="text-xs text-slate-500 mt-1">
              {profitableTrades} wins / {lossTrades} losses
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Total Profit</div>
            <div className="text-3xl font-bold text-green-400">
              +${totalProfit.toFixed(2)}
            </div>
            <div className="text-xs text-slate-500 mt-1">From Profitable Trades</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Total Loss</div>
            <div className="text-3xl font-bold text-red-400">
              -${totalLoss.toFixed(2)}
            </div>
            <div className="text-xs text-slate-500 mt-1">From Loss-Making Trades</div>
          </div>
        </div>

        {/* Net P&L */}
        <div className="mt-6 rounded-lg p-4 text-center" style={{ backgroundColor: '#0f100f', border: '1px solid white' }}>
          <div className="text-sm text-slate-400 mb-2">Net Profit/Loss</div>
          <div className={`text-4xl font-bold ${totalProfit - totalLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {totalProfit - totalLoss >= 0 ? '+' : ''}{(totalProfit - totalLoss).toFixed(2)}%
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Overall Trading Performance
          </div>
        </div>
      </div>

      {/* Trade Status Breakdown */}
      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-6">Trade Status Breakdown</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400">Active Trades</span>
              <span className="text-purple-400 font-bold">{activeTrades}</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div 
                className="bg-purple-500 h-2 rounded-full" 
                style={{ width: `${totalTrades > 0 ? (activeTrades / totalTrades) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400">Completed Trades</span>
              <span className="text-green-400 font-bold">{completedTrades}</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full" 
                style={{ width: `${totalTrades > 0 ? (completedTrades / totalTrades) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400">Failed Trades</span>
              <span className="text-red-400 font-bold">{failedTrades}</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div 
                className="bg-red-500 h-2 rounded-full" 
                style={{ width: `${totalTrades > 0 ? (failedTrades / totalTrades) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Trading Performance Analysis */}
      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-6">Trading Performance Analysis</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <h3 className="text-lg font-semibold text-white mb-4">Average Performance</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Avg Profit per Trade:</span>
                <span className={`font-semibold ${avgProfitPerTrade >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {avgProfitPerTrade >= 0 ? '+' : ''}{avgProfitPerTrade.toFixed(2)}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Total Trades:</span>
                <span className="text-blue-400 font-semibold">{totalTrades}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Success Rate:</span>
                <span className={`font-semibold ${winRate >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                  {winRate.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <h3 className="text-lg font-semibold text-white mb-4">Best Trade</h3>
            {bestTrade ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Token:</span>
                  <span className="text-blue-400 font-semibold">{bestTrade.symbol || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Profit:</span>
                  <span className="text-green-400 font-semibold">+{calculateProfitLoss(bestTrade).toFixed(2)}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Amount:</span>
                  <span className="text-white font-semibold">{bestTrade.amount || '0'} SOL</span>
                </div>
              </div>
            ) : (
              <p className="text-slate-400 text-center">No completed trades yet</p>
            )}
          </div>

          <div className="backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <h3 className="text-lg font-semibold text-white mb-4">Worst Trade</h3>
            {worstTrade ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Token:</span>
                  <span className="text-blue-400 font-semibold">{worstTrade.symbol || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Loss:</span>
                  <span className="text-red-400 font-semibold">{calculateProfitLoss(worstTrade).toFixed(2)}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Amount:</span>
                  <span className="text-white font-semibold">{worstTrade.amount || '0'} SOL</span>
                </div>
              </div>
            ) : (
              <p className="text-slate-400 text-center">No completed trades yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Trading Strategy Insights */}
      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-6">Trading Strategy Insights</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="backdrop-blur-sm rounded-lg p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <h3 className="text-lg font-semibold text-white mb-4">Strategy Performance</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Risk Level:</span>
                <span className={`font-semibold ${
                  winRate >= 70 ? 'text-green-400' : 
                  winRate >= 50 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {winRate >= 70 ? 'Low Risk' : winRate >= 50 ? 'Medium Risk' : 'High Risk'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Profitability:</span>
                <span className={`font-semibold ${totalProfit - totalLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totalProfit - totalLoss >= 0 ? 'Profitable' : 'Loss-Making'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Trade Frequency:</span>
                <span className="text-blue-400 font-semibold">
                  {totalTrades > 0 ? `${(totalTrades / Math.max(1, (Date.now() - new Date(filteredTrades[0]?.buy_time || Date.now()).getTime()) / (1000 * 60 * 60 * 24))).toFixed(1)} trades/day` : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <div className="backdrop-blur-sm rounded-lg p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <h3 className="text-lg font-semibold text-white mb-4">Recommendations</h3>
            <div className="space-y-3">
              {winRate >= 70 ? (
                <p className="text-green-400 text-sm">
                  ✅ Your strategy shows excellent performance. Consider increasing position sizes gradually.
                </p>
              ) : winRate >= 50 ? (
                <p className="text-yellow-400 text-sm">
                  ⚠️ Your strategy shows moderate performance. Consider refining your entry/exit criteria.
                </p>
              ) : (
                <p className="text-red-400 text-sm">
                  ❌ Your strategy needs improvement. Analyze failed trades and adjust parameters.
                </p>
              )}
              
              {totalProfit - totalLoss >= 0 ? (
                <p className="text-green-400 text-sm">
                  💰 Your strategy is profitable. Monitor for consistency over time.
                </p>
              ) : (
                <p className="text-red-400 text-sm">
                  📉 Your strategy is losing money. Review stop-loss and take-profit settings.
                </p>
              )}
              
              <p className="text-blue-400 text-sm">
                🔄 Continue trading to gather more data and improve your strategy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsPage;
